<script>
  import { onMount } from 'svelte';
  import AgGridSvelte from 'ag-grid-svelte';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  import { API_BASE_URL, isSuperAdmin, isAdminUser } from './config.js';
  import FormModal from './components/modals/FormModal.svelte';
  import ConfirmModal from './components/modals/ConfirmModal.svelte';
  import Button from './components/common/Button.svelte';
  import Header from './components/layout/Header.svelte';
  import './styles/main.css';

  export let setPage;
  export let user;
  export let onLogout = null; // App.svelte의 handleLogout 함수

  // 권한 확인
  let isAdmin = false;
  let isCompany = false;

  // 관리자 계정인지 확인하는 함수
  function isAdminAccount(user) {
    return user && isSuperAdmin(user.email);
  }

  // 선택된 사용자들 중 관리자 계정이 포함되어 있는지 확인
  function hasAdminInSelection(users) {
    return users.some(user => isAdminAccount(user));
  }

  if (typeof window !== 'undefined') {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user = JSON.parse(storedUser);
        isAdmin = isAdminUser(user);
        isCompany = user.role === 'company';
      } catch (e) {
        console.error('사용자 정보 파싱 오류:', e);
      }
    }
  }

  // AG Grid 설정
  let rowData = [];
  let gridApi;
  let selectedUser = null;
  let selectedUsers = []; // 다중 선택을 위한 배열 추가
  let isCreateModalOpen = false;
  let isEditModalOpen = false;
  let isDeleteModalOpen = false;
  let isBulkDeleteModalOpen = false; // 일괄 삭제 모달 추가
  // 회원 생성 폼 데이터
  let createForm = {
    email: '',
    password: '',
    name: '',
    phone: '',
    birthDate: '',
    gender: '', // 'M' | 'F'
    member_type: 'individual',
    is_blacklist: false,
    blacklist_reason: '',
    available_from: ''
  };

  // 회원 수정 폼 데이터
  let editForm = {
    id: null,
    email: '',
    name: '',
    phone: '',
    birthDate: '',
    gender: '',
    member_type: 'individual',
    is_blacklist: false,
    blacklist_reason: '',
    available_from: ''
  };

  let quickFilterText = '';
  let showDeletedUsers = false;

  function formatPhoneNumber(phone) {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length < 9) return phone; // 형식이 맞지 않으면 원본 반환
    return digits.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
  }

  // 날짜 값을 'YYYY-MM-DD' 형식으로 변환 (date input에 사용)
  function formatDateForInput(dateValue) {
    if (!dateValue) return '';
    // 이미 올바른 형식이면 그대로 반환
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    const d = new Date(dateValue);
    if (isNaN(d)) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  const desktopColumnDefs = [
    {
      headerName: '',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: false,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      lockPosition: true,
      cellClass: 'checkbox-cell'
    },
    { 
      headerName: '이름', 
      field: 'name', 
      sortable: true, 
      filter: true,
      width: 120,
      flex: 0,
      cellRenderer: nameCellRenderer,
      filterValueGetter: params => {
        // 이름과 이메일로 모두 필터링 가능하도록 설정
        const name = params.data.name || '';
        const email = params.data.email || '';
        return `${name} ${email}`;
      }
    },
    {
      headerName: '출생년도',
      field: 'birth_date',
      sortable: true,
      filter: true,
      width: 110,
      valueFormatter: params => {
        if (!params.value) return '';
        const str = String(params.value);
        if (/^\d{4}/.test(str)) return str.slice(0, 4);
        const d = new Date(params.value);
        return isNaN(d) ? '' : d.getFullYear();
      }
    },
    {
      headerName: '성별',
      field: 'gender',
      sortable: true,
      filter: true,
      width: 90,
      valueFormatter: params => params.value === 'M' ? '남' : params.value === 'F' ? '여' : ''
    },
    {
      headerName: '연락처',
      field: 'phone',
      sortable: true,
      filter: true,
      valueFormatter: params => formatPhoneNumber(params.value)
    },
    { headerName: '이메일', field: 'email', sortable: true, filter: true },
    {
      headerName: '블랙',
      field: 'is_blacklist',
      sortable: true,
      filter: true,
      width: 90,
      valueFormatter: params => (params.value === true || params.value === 'Y') ? '예' : '아니오'
    },
    {
      headerName: '제외',
      field: 'is_excluded',
      sortable: true,
      filter: true,
      width: 90,
      valueFormatter: params => (params.value === true) ? '제외됨' : '활성'
    },
    {
      headerName: '삭제',
      field: 'is_deleted',
      sortable: true,
      filter: true,
      width: 90,
      valueFormatter: params => (params.value === true) ? '삭제됨' : '정상'
    },
    { headerName: '비고', field: 'blacklist_reason', sortable: false, filter: true, flex: 1 },
    {
      headerName: '투입가능일자',
      field: 'available_from',
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: params => {
        if (!params.value) return '';
        const str = String(params.value);
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
        const d = new Date(params.value);
        if (isNaN(d)) return str;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
    },
    {
      headerName: '등록일',
      field: 'created_at',
      sortable: true,
      filter: true,
      width: 120,
      sort: 'desc',
      sortIndex: 0,
      valueFormatter: params => {
        if (!params.value) return '';
        const str = String(params.value);
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
        const d = new Date(params.value);
        if (isNaN(d)) return str;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      }
    },
    {
      headerName: '첨부 문서',
      field: 'attachments',
      width: 140,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        const hasFiles = params.data && params.data.hasFiles === true;
        const bg = hasFiles ? '#4A90E2' : '#6c757d';
        const title = hasFiles ? '첨부 문서 있음 - 클릭하여 확인' : '첨부 문서 없음';
        return `<button class="profile-docs-btn" style="background:${bg};color:#fff;" title="${title}"><span class="doc-icon">📁</span> 문서</button>`;
      }
    },
    {
      headerName: '최종로그인일시',
      field: 'last_login_at',
      sortable: true,
      filter: true,
      width: 140,
      valueFormatter: params => {
        if (!params.value) return '';
        const d = new Date(params.value);
        if (isNaN(d)) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day} ${h}:${min}`;
      }
    },
  ];

  let isLoading = false;
  let loadError = null;

  // 공통 API 응답 처리 함수
  function handleApiResponse(response) {
    if (response.status === 401 || response.status === 403) {
      console.warn('인증 실패. 로그인 페이지로 이동합니다.');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      alert('세션이 만료되었습니다. 다시 로그인해주세요.');
      window.location.href = '/?t=' + Date.now() + '#login';
      return false;
    }
    return true;
  }

  onMount(() => {
    // 권한 및 토큰 체크
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
      setPage('login');
      return;
    }

    if (!isAdmin) {
      console.warn('관리자 권한이 없습니다. 홈페이지로 이동합니다.');
      setPage('home');
      return;
    }

    fetchUsers();
  });

  async function fetchUsers() {
    try {
      isLoading = true;
      loadError = null;

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('토큰이 없습니다. 로그인 페이지로 이동합니다.');
        setPage('login');
        return;
      }

      const endpoint = showDeletedUsers ? 
        `${API_BASE_URL}/api/users/deleted` : 
        `${API_BASE_URL}/api/users`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // 공통 인증 오류 처리
        if (!handleApiResponse(response)) return;
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || '사용자 목록을 불러오지 못했습니다.');
      }

      const data = await response.json();
      // 기본 hasFiles false로 초기화
      rowData = (data.users || []).map(u => ({ ...u, hasFiles: false }));
      
      // 그리드가 준비되었을 때 첨부파일 존재 여부 확인
      if (gridApi) {
        await initHasFilesFlags();
      }
    } catch (error) {
      loadError = error.message;
      console.error('사용자 목록 조회 오류:', error);
    } finally {
      isLoading = false;
    }
  }

  // 로그아웃 함수 - App.svelte의 handleLogout 사용
  function handleLogout() {
    // 드롭다운 메뉴 닫기
    isDropdownOpen = false;
    isApplicantMenuOpen = false;
    isMenuOpen = false;
    
    // App.svelte의 handleLogout 호출하여 전체 상태 초기화
    if (onLogout) {
      onLogout();
    } else {
      // fallback: onLogout이 없을 경우 직접 처리
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setPage('home');
    }
  }

  // 드롭다운 메뉴 토글
  let isDropdownOpen = false;
  let isApplicantMenuOpen = false;
  function toggleDropdown() {
    isDropdownOpen = !isDropdownOpen;
  }
  
  function toggleApplicantMenu() {
    isApplicantMenuOpen = !isApplicantMenuOpen;
  }

  // 모바일 메뉴 토글
  let isMenuOpen = false;
  function toggleMenu() {
    isMenuOpen = !isMenuOpen;
  }

  function handleOverlayKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') {
      event.preventDefault();
      toggleMenu();
    }
  }

  function onGridReady(params) {
    gridApi = params.api;
    
    // 그리드가 준비되고 데이터가 있으면 첨부파일 존재 여부 확인
    if (rowData && rowData.length > 0) {
      initHasFilesFlags();
    }
  }

  function onSelectionChanged() {
    const selectedRows = gridApi ? gridApi.getSelectedRows() : [];
    selectedUser = selectedRows[0] || null;
    selectedUsers = selectedRows;
    console.log('선택된 사용자 수:', selectedUsers.length);
    console.log('선택된 사용자들:', selectedUsers.map(u => u.name || u.email));
  }

  // 모달 열기/닫기
  function openCreateModal() {
    createForm = { 
      email: '', password: '', name: '', phone: '', 
      birthDate: '', gender: '', member_type: 'individual', 
      is_blacklist: false, blacklist_reason: '', available_from: '' 
    };
    isCreateModalOpen = true;
  }

  function openEditModal() {
    if (!selectedUser) return;
    editForm = {
      ...selectedUser,
      birthDate: formatDateForInput(selectedUser.birth_date),
      gender: selectedUser.gender || '',
      member_type: selectedUser.member_type || 'individual',
      is_blacklist: selectedUser.is_blacklist === true || selectedUser.is_blacklist === 'Y',
      blacklist_reason: selectedUser.blacklist_reason || '',
      available_from: formatDateForInput(selectedUser.available_from)
    };
    isEditModalOpen = true;
  }

  function openDeleteModal() {
    if (!selectedUser) return;
    if (isAdminAccount(selectedUser)) {
      alert('관리자 계정은 삭제할 수 없습니다.');
      return;
    }
    isDeleteModalOpen = true;
  }

  function toggleUserExcludeStatus() {
    if (!selectedUser) return;
    updateUserExcludeStatus();
  }

  // API 호출 함수
  async function createUser() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || '회원 생성 실패');
      }
      isCreateModalOpen = false;
      fetchUsers();
    } catch (e) {
      alert(e.message);
    }
  }

  async function updateUser() {
    try {
      const token = localStorage.getItem('token');
      const { id, email, ...updates } = editForm;
      const payload = {
        ...updates,
        isBlacklist: updates.is_blacklist !== undefined ? updates.is_blacklist : undefined,
        blacklistReason: updates.blacklist_reason,
        available_from: updates.available_from
      };
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || '회원 수정 실패');
      }
      isEditModalOpen = false;
      fetchUsers();
    } catch (e) {
      alert(e.message);
    }
  }

  async function deleteUser() {
    try {
      if (isAdminAccount(selectedUser)) {
        alert('관리자 계정은 삭제할 수 없습니다.');
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        if (!handleApiResponse(response)) return;
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || '회원 삭제 실패');
      }
      isDeleteModalOpen = false;
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(e.message);
    }
  }

  async function updateUserExcludeStatus() {
    try {
      const token = localStorage.getItem('token');
      const newExcludeStatus = !selectedUser.is_excluded; // 현재 상태의 반대로 토글
      
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}/exclude`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isExcluded: newExcludeStatus
        })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || '제외 상태 업데이트 실패');
      }
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(e.message);
    }
  }

  $: if (gridApi) {
    gridApi.setQuickFilter(quickFilterText);
  }

  // 첨부 문서 조회 관련 상태 변수
  let isProfileFilesModalOpen = false;
  let selectedUserFiles = [];
  let isLoadingFiles = false;
  let fileLoadError = null;
  let fileModalUser = null;

  // 파일 업로드 관련 상태 변수
  let uploadFileInput;
  let selectedUploadFiles = [];
  let isUploading = false;
  let uploadErrors = [];
  let dragOver = false;
  let isUploadSectionOpen = false; // 업로드 영역 펼침/접힘 상태 (기본: 접힘)

  // 셀 클릭 이벤트 – 첨부 문서 버튼 처리
  function onCellClicked(event) {
    // 모바일 뷰: 카드 내 '문서' 버튼 클릭 감지
    if (isMobile) {
      const clickedEl = event.event.target;
      if (clickedEl && clickedEl.closest('.js-mobile-docs-btn')) {
        fetchUserFiles(event.data);
        return;
      }
    }

    // 데스크톱 뷰: '첨부 문서' 셀 클릭 감지
    if (!isMobile && event.column && event.column.getColId && event.column.getColId() === 'attachments') {
      const userData = event.data;
      if (userData) {
        fetchUserFiles(userData);
      }
    }
  }

  // ==================== 첨부 문서 관련 함수 ====================
  async function fetchUserFiles(user) {
    if (!user || !user.id) {
      console.error('사용자 ID가 없습니다:', user);
      return;
    }
    try {
      isLoadingFiles = true;
      fileLoadError = null;
      selectedUserFiles = [];
      fileModalUser = user;

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${user.id}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('파일 목록을 불러오는 데 실패했습니다.');
      }

      const data = await response.json();
      if (data.success && data.files) {
        selectedUserFiles = data.files;
        // 파일 존재 여부 저장 후 그리드 리프레시
        const hasFiles = data.files.length > 0;
        rowData = rowData.map(u => u.id === user.id ? { ...u, hasFiles } : u);
        if (gridApi) {
          gridApi.setRowData(rowData);
          // 첨부 문서 컬럼만 리프레시
          gridApi.refreshCells({
            columns: ['attachments'],
            force: true
          });
          if (isMobile) gridApi.resetRowHeights();
        }
      } else {
        selectedUserFiles = [];
        rowData = rowData.map(u => u.id === user.id ? { ...u, hasFiles: false } : u);
        if (gridApi) {
          gridApi.setRowData(rowData);
          // 첨부 문서 컬럼만 리프레시
          gridApi.refreshCells({
            columns: ['attachments'],
            force: true
          });
          if (isMobile) gridApi.resetRowHeights();
        }
      }
      isProfileFilesModalOpen = true;
    } catch (error) {
      console.error('사용자 파일 조회 오류:', error);
      fileLoadError = error.message;
      isProfileFilesModalOpen = true;
    } finally {
      isLoadingFiles = false;
    }
  }

  async function downloadFile(file, user) {
    try {
      const userId = user?.id || fileModalUser?.id;
      if (!userId) {
        throw new Error('사용자 정보가 올바르지 않습니다.');
      }
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${userId}/stream/${file.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('파일 다운로드에 실패했습니다.');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.original_name || `download_${file.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('파일 다운로드 오류:', error);
      alert(`파일 다운로드 실패: ${error.message}`);
    }
  }

  function closeProfileFilesModal() {
    isProfileFilesModalOpen = false;
    selectedUserFiles = [];
    fileLoadError = null;
    // 업로드 상태 초기화
    selectedUploadFiles = [];
    uploadErrors = [];
    dragOver = false;
    isUploadSectionOpen = false;
  }

  // ==================== 파일 업로드 관련 함수 ====================
  function handleUploadFileSelect(event) {
    const files = Array.from(event.target.files);
    processUploadFiles(files);
  }

  function handleUploadDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    dragOver = true;
  }

  function handleUploadDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    dragOver = false;
  }

  function handleUploadDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    dragOver = false;
    const files = Array.from(event.dataTransfer.files);
    processUploadFiles(files);
  }

  function processUploadFiles(files) {
    uploadErrors = [];
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (files.length > maxFiles) {
      uploadErrors = [`최대 ${maxFiles}개의 파일만 업로드할 수 있습니다.`];
      return;
    }

    const validFiles = [];
    for (const file of files) {
      if (file.size > maxSize) {
        uploadErrors = [...uploadErrors, `${file.name}: 파일 크기가 10MB를 초과합니다.`];
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      selectedUploadFiles = validFiles;
    }
  }

  function removeUploadFile(index) {
    selectedUploadFiles = selectedUploadFiles.filter((_, i) => i !== index);
  }

  function formatUploadFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function getUploadFileIcon(fileName) {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'hwp':
      case 'hwpx': return '📃';
      case 'xls':
      case 'xlsx': return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'txt': return '📃';
      default: return '📎';
    }
  }

  // 파일 삭제 함수
  async function deleteFileForUser(file) {
    if (!file || !fileModalUser) return;

    const confirmDelete = confirm(`"${file.original_name}" 파일을 삭제하시겠습니까?`);
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${fileModalUser.id}/files/${file.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || '파일 삭제에 실패했습니다.');
      }

      // 삭제 성공 - 파일 목록 새로고침
      await fetchUserFiles(fileModalUser);
      alert('파일이 삭제되었습니다.');

    } catch (error) {
      console.error('파일 삭제 오류:', error);
      alert(`파일 삭제 실패: ${error.message}`);
    }
  }

  async function uploadFilesForUser() {
    if (selectedUploadFiles.length === 0 || !fileModalUser) return;

    isUploading = true;
    uploadErrors = [];

    try {
      const token = localStorage.getItem('token');
      const userId = fileModalUser.id;

      for (const file of selectedUploadFiles) {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('fileType', 'document');
        formData.append('description', `관리자가 업로드: ${file.name}`);

        const response = await fetch(`${API_BASE_URL}/api/files/admin/user/${userId}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `${file.name} 업로드 실패`);
        }
      }

      // 업로드 성공 - 파일 목록 새로고침
      selectedUploadFiles = [];
      if (uploadFileInput) uploadFileInput.value = '';
      await fetchUserFiles(fileModalUser);
      alert('파일 업로드가 완료되었습니다.');

    } catch (error) {
      console.error('파일 업로드 오류:', error);
      uploadErrors = [error.message || '파일 업로드 중 오류가 발생했습니다.'];
    } finally {
      isUploading = false;
    }
  }

  // ==================== 초기 첨부 파일 존재 여부 확인 ====================
  async function initHasFilesFlags() {
    try {
      const token = localStorage.getItem('token');
      if (!token || !rowData || rowData.length === 0) return;

      // 파일 존재 여부 확인을 위한 병렬 요청
      const promises = rowData.map(async (u) => {
        if (!u.id) return { ...u, hasFiles: false };
        
        try {
          const res = await fetch(`${API_BASE_URL}/api/files/admin/user/${u.id}/files?limit=1`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (res.ok) {
            const d = await res.json();
            let hasFiles = false;
            
            if (d && typeof d === 'object') {
              if (Array.isArray(d.files)) {
                hasFiles = d.files.length > 0;
              } else if (Array.isArray(d)) {
                hasFiles = d.length > 0;
              }
            }
            
            return { ...u, hasFiles };
          }
        } catch (err) {
          console.warn(`파일 존재 여부 확인 실패 (사용자 ID: ${u.id}):`, err);
        }
        
        return { ...u, hasFiles: false };
      });

      // 모든 요청 완료 후 새로운 배열로 업데이트
      const updatedRowData = await Promise.all(promises);
      rowData = updatedRowData;
      
      // 그리드 업데이트 및 리프레시
      if (gridApi) {
        gridApi.setRowData(rowData);
        // 첨부 문서 컬럼만 리프레시
        gridApi.refreshCells({
          columns: ['attachments'],
          force: true
        });
        
        if (isMobile) {
          gridApi.resetRowHeights();
        }
      }
    } catch (e) {
      console.warn('initHasFilesFlags 오류:', e);
    }
  }

  // === 반응형(모바일) 지원을 위한 변수 및 함수 추가 ===
  let isMobile = false;

  function checkMobileDevice() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent.toLowerCase();
    const keywords = ['android', 'iphone', 'ipad', 'ipod', 'windows phone', 'mobile'];
    return keywords.some(k => ua.includes(k)) || window.innerWidth < 768;
  }

  function handleResize() {
    const prev = isMobile;
    isMobile = checkMobileDevice();
    if (gridApi && prev !== isMobile) {
      // 레이아웃 변화 시 그리드 높이 재계산
      gridApi.setDomLayout(isMobile ? 'autoHeight' : 'normal');
      gridApi.resetRowHeights();
      gridApi.setRowData(rowData);
    }
  }

  onMount(() => {
    isMobile = checkMobileDevice();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  // ======= 모바일 카드 렌더러 =======
  function userCardRenderer(params) {
    const d = params.data;
    if (!d) return '';
    const genderText = d.gender === 'M' ? '남' : d.gender === 'F' ? '여' : '-';
    const birthYear = d.birth_date ? (String(d.birth_date).slice(0, 4)) : '-';
    const phone = formatPhoneNumber(d.phone);
    const isBl = d.is_blacklist === true || d.is_blacklist === 'Y';
    const isExcluded = d.is_excluded === true;
    const isDeleted = d.is_deleted === true;
    const blTag = isBl ? '<div class="member-blacklist-tag">Blacklist</div>' : '';
    const excludedTag = isExcluded ? '<div class="member-excluded-tag">제외됨</div>' : '';
    const deletedTag = isDeleted ? '<div class="member-deleted-tag">삭제됨</div>' : '';

    const remarkRow = d.blacklist_reason ? `<div class="card-row"><strong>비고:</strong> ${d.blacklist_reason}</div>` : '';
    const statusRow = `<div class="card-row"><strong>상태:</strong> ${isBl ? '블랙' : '정상'} / ${isExcluded ? '제외됨' : '활성'} / ${isDeleted ? '삭제됨' : '정상'}</div>`;
    
    // 투입가능일자 포맷팅
    const availableFromText = d.available_from ? String(d.available_from).slice(0, 10) : '-';
    const availableFromRow = `<div class="card-row"><strong>투입가능일자:</strong> ${availableFromText}</div>`;
    
    // 최종로그인일시 포맷팅
    const lastLoginText = d.last_login_at ? (() => {
      const dt = new Date(d.last_login_at);
      if (isNaN(dt)) return '-';
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      const h = String(dt.getHours()).padStart(2, '0');
      const min = String(dt.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day} ${h}:${min}`;
    })() : '-';
    const lastLoginRow = `<div class="card-row"><strong>최종로그인일시:</strong> ${lastLoginText}</div>`;
    
    // 첨부 문서 버튼 배경색 결정 (파일 있으면 파란색, 없으면 회색)
    const attachBtnBg = d.hasFiles === true ? '#4A90E2' : '#6c757d';
    const attachBtnText = d.hasFiles === true ? '📁 문서' : '📁 문서';
    const attachmentButtonRow = `
      <div class="card-row attachment-row">
        <strong>첨부 문서:</strong>
        <button class="profile-docs-btn js-mobile-docs-btn" style="background:${attachBtnBg}; color:#fff;">
          ${attachBtnText}
        </button>
      </div>
    `;

    return `
      <div class="mobile-card">
        <div class="card-header">
          <div class="checkbox-container">
            <input type="checkbox" class="mobile-checkbox" data-user-id="${d.id}" />
          </div>
          <div class="name">${d.name || d.email}</div>
          <div class="badges">
            ${blTag}
            ${excludedTag}
            ${deletedTag}
          </div>
        </div>
        <div class="card-body">
          <div class="card-row"><strong>출생년도:</strong> ${birthYear}</div>
          <div class="card-row"><strong>성별:</strong> ${genderText}</div>
          <div class="card-row"><strong>연락처:</strong> ${phone || '-'}</div>
          <div class="card-row"><strong>이메일:</strong> ${d.email}</div>
          ${statusRow}
          ${remarkRow}
          ${availableFromRow}
          ${lastLoginRow}
          ${attachmentButtonRow}
        </div>
      </div>
    `;
  }

  // ======= 이름 셀 렌더러 (데스크톱) =======
  function nameCellRenderer(params) {
    const name = params.value || '';
    const isBl = params.data && (params.data.is_blacklist === true || params.data.is_blacklist === 'Y');
    const isExcluded = params.data && params.data.is_excluded === true;
    const isDeleted = params.data && params.data.is_deleted === true;
    const blTag = isBl ? '<div class="member-blacklist-tag">Blacklist</div>' : '';
    const excludedTag = isExcluded ? '<div class="member-excluded-tag">제외됨</div>' : '';
    const deletedTag = isDeleted ? '<div class="member-deleted-tag">삭제됨</div>' : '';
    return `<div class="name-cell">${name}<div class="badges">${blTag}${excludedTag}${deletedTag}</div></div>`;
  }

  // ======= 모바일 전용 columnDefs =======
  const mobileColumnDefs = [
    {
      headerName: '회원 정보',
      field: 'id',
      minWidth: 300,
      flex: 1,
      cellRenderer: 'userCardRenderer',
      autoHeight: true,
      wrapText: true,
      sortable: false,
      editable: false,
      checkboxSelection: false // 모바일에서는 카드 내부 체크박스 사용
    }
  ];

  // ======= 반응형 columnDefs, grid 옵션 =======
  $: columnDefs = isMobile ? mobileColumnDefs : desktopColumnDefs;

  $: paginationPageSize = isMobile ? 5 : 20;

  $: gridOptions = {
    domLayout: isMobile ? 'autoHeight' : 'normal',
    rowSelection: 'multiple', // 다중 선택 모드로 변경
    components: { userCardRenderer },
    defaultColDef: { resizable: true },
    suppressRowClickSelection: true, // 행 클릭으로 선택되는 것을 방지 (체크박스만으로 선택)
    suppressRowDeselection: false, // 선택 해제 허용
    getRowId: (params) => params.data.id, // 각 행을 고유하게 식별
    deltaSort: true, // 정렬 시 선택 상태 유지
    suppressClearOnFillReduction: true, // 데이터 변경 시 선택 상태 유지
    rowHeight: 50
  };

  $: {
    // 모달이나 모바일 메뉴가 열려 있을 때 배경 스크롤 방지
    if (isProfileFilesModalOpen || isCreateModalOpen || isEditModalOpen || isDeleteModalOpen || isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  // 일괄 처리 함수들 추가
  function openBulkDeleteModal() {
    if (selectedUsers.length === 0) return;
    if (hasAdminInSelection(selectedUsers)) {
      alert('관리자 계정이 선택에 포함되어 있어 일괄 삭제를 진행할 수 없습니다.');
      return;
    }
    isBulkDeleteModalOpen = true;
  }

  async function bulkDeleteUsers() {
    try {
      // 관리자 계정 필터링
      const usersToDelete = selectedUsers.filter(user => !isAdminAccount(user));
      
      if (usersToDelete.length === 0) {
        alert('삭제할 수 있는 사용자가 없습니다.');
        return;
      }
      
      if (usersToDelete.length !== selectedUsers.length) {
        alert('관리자 계정은 삭제에서 제외됩니다.');
      }
      
      const token = localStorage.getItem('token');
      const userIds = usersToDelete.map(user => user.id);
      
      const promises = userIds.map(id => 
        fetch(`${API_BASE_URL}/api/users/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      );

      const results = await Promise.all(promises);
      const failedDeletes = results.filter(r => !r.ok);
      
      if (failedDeletes.length > 0) {
        alert(`${failedDeletes.length}개의 회원 삭제에 실패했습니다.`);
      } else {
        alert(`${usersToDelete.length}개의 회원이 삭제되었습니다.`);
      }
      
      isBulkDeleteModalOpen = false;
      selectedUsers = [];
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(`일괄 삭제 중 오류가 발생했습니다: ${e.message}`);
    }
  }

  // 사용자 복구 함수 추가
  async function restoreUser() {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/users/${selectedUser.id}/restore`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || '회원 복구 실패');
      }
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(e.message);
    }
  }

  async function bulkUpdateExcludeStatus(shouldExclude) {
    try {
      const token = localStorage.getItem('token');
      const userIds = selectedUsers.map(user => user.id);
      
      const promises = userIds.map(id => 
        fetch(`${API_BASE_URL}/api/users/${id}/exclude`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isExcluded: shouldExclude
          })
        })
      );

      const results = await Promise.all(promises);
      const failedUpdates = results.filter(r => !r.ok);
      
      if (failedUpdates.length > 0) {
        alert(`${failedUpdates.length}개의 회원 상태 변경에 실패했습니다.`);
      } else {
        const action = shouldExclude ? '제외' : '제외 해제';
        alert(`${selectedUsers.length}개의 회원이 ${action} 처리되었습니다.`);
      }
      
      selectedUsers = [];
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(`일괄 처리 중 오류가 발생했습니다: ${e.message}`);
    }
  }

  async function bulkUpdateBlacklistStatus(shouldBlacklist) {
    try {
      const token = localStorage.getItem('token');
      const userIds = selectedUsers.map(user => user.id);
      
      const promises = userIds.map(id => 
        fetch(`${API_BASE_URL}/api/users/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            isBlacklist: shouldBlacklist,
            blacklistReason: shouldBlacklist ? '일괄 처리' : ''
          })
        })
      );

      const results = await Promise.all(promises);
      const failedUpdates = results.filter(r => !r.ok);
      
      if (failedUpdates.length > 0) {
        alert(`${failedUpdates.length}개의 회원 블랙 상태 변경에 실패했습니다.`);
      } else {
        const action = shouldBlacklist ? '블랙 등록' : '블랙 해제';
        alert(`${selectedUsers.length}개의 회원이 ${action} 처리되었습니다.`);
      }
      
      selectedUsers = [];
      selectedUser = null;
      fetchUsers();
    } catch (e) {
      alert(`일괄 처리 중 오류가 발생했습니다: ${e.message}`);
    }
  }

  // 모바일 체크박스 처리 함수 추가
  function handleMobileCheckboxChange(event) {
    const checkbox = event.target;
    const userId = parseInt(checkbox.dataset.userId);
    const isChecked = checkbox.checked;
    
    if (isChecked) {
      // 체크된 경우: 해당 사용자를 선택 목록에 추가
      const user = rowData.find(u => u.id === userId);
      if (user && !selectedUsers.find(u => u.id === userId)) {
        selectedUsers = [...selectedUsers, user];
      }
    } else {
      // 체크 해제된 경우: 선택 목록에서 제거
      selectedUsers = selectedUsers.filter(u => u.id !== userId);
    }
    
    // 첫 번째 선택된 사용자를 selectedUser로 설정
    selectedUser = selectedUsers[0] || null;
  }

  // 모바일 체크박스 이벤트 리스너 추가
  function setupMobileCheckboxListeners() {
    if (!isMobile) return;
    
    setTimeout(() => {
      const checkboxes = document.querySelectorAll('.mobile-checkbox');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', handleMobileCheckboxChange);
      });
    }, 100);
  }

  // 그리드 데이터 변경 시 모바일 체크박스 리스너 재설정
  $: if (isMobile && rowData) {
    setupMobileCheckboxListeners();
  }

  // 엑셀 다운로드 함수
  async function downloadExcel() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
        return;
      }

      // 전체 사용자 데이터 가져오기 (페이지 제한 없이)
      const response = await fetch(`${API_BASE_URL}/api/users/export-excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || '엑셀 다운로드에 실패했습니다.');
      }

      // 블롭으로 응답 받기
      const blob = await response.blob();
      
      // 다운로드 링크 생성
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 파일명 설정 (현재 날짜 포함)
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.download = `회원목록_${dateStr}.xlsx`;
      
      // 다운로드 실행
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 메모리 정리
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      alert(`엑셀 다운로드 실패: ${error.message}`);
    }
  }
</script>

<svelte:head>
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/ag-grid-community/styles/ag-grid.css" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/ag-grid-community/styles/ag-theme-alpine.css" />
</svelte:head>

<main class="main-container">
  <!-- 상단 네비게이션 -->
  <Header
    {user}
    {isAdmin}
    {isCompany}
    currentPage="admin-users"
    {isDropdownOpen}
    {isApplicantMenuOpen}
    onPageChange={setPage}
    onLogout={handleLogout}
    onToggleDropdown={toggleDropdown}
    onToggleMenu={toggleMenu}
    onToggleApplicantMenu={toggleApplicantMenu}
    onLogoClick={() => setPage('home')}
  />

  <!-- 모바일 메뉴 오버레이 -->
  {#if isMenuOpen}
    <div
      class="mobile-menu-overlay"
      on:click={toggleMenu}
      on:keydown={handleOverlayKeydown}
      role="button"
      tabindex="0"
    ></div>
  {/if}

  <!-- 모바일 메뉴 -->
  <div class="mobile-menu" class:open={isMenuOpen}>
    <button class="close-btn" on:click={toggleMenu}>✕</button>
    <ul>
      {#if user}
        <li class="user-info">{isAdmin ? '[관리자] ' : isCompany ? '[기업] ' : ''}{user.name || user.email.split('@')[0]}님</li>
        <li><a href="/mypage" on:click|preventDefault={() => { setPage('mypage'); toggleMenu(); }}>마이페이지</a></li>
        <li><a href="/home" on:click|preventDefault={() => { setPage('home'); toggleMenu(); }}>프로젝트 찾기</a></li>
        <li><a href="/how-to-use" on:click|preventDefault={() => { setPage('how-to-use'); toggleMenu(); }}>이용방법</a></li>
        {#if isAdmin}
          <li><a href="/admin-dashboard" on:click|preventDefault={() => { setPage('admin-dashboard'); toggleMenu(); }}>대시보드</a></li>
          <li><a href="/admin-users" on:click|preventDefault={() => { setPage('admin-users'); toggleMenu(); }}>회원 관리</a></li>
          <li><a href="/admin-applicants" on:click|preventDefault={() => setPage('admin-applicants')}>지원자 관리</a></li>
          <li><a href="/project-register" on:click|preventDefault={() => { setPage('project-register'); toggleMenu(); }}>공고 등록</a></li>
        {/if}
        {#if isCompany && !isAdmin}
          <li><a href="/company-applicants" on:click|preventDefault={() => { setPage('company-applicants'); toggleMenu(); }}>지원자 관리</a></li>
          <li><a href="/project-register" on:click|preventDefault={() => { setPage('project-register'); toggleMenu(); }}>공고 등록</a></li>
        {/if}
        <li><a href="/logout" on:click|preventDefault={() => { handleLogout(); toggleMenu(); }}>로그아웃</a></li>
      {:else}
        <li><a href="/login" on:click|preventDefault={() => { setPage('login'); toggleMenu(); }}>로그인/회원가입</a></li>
      {/if}
    </ul>
  </div>

  <div class="admin-content">
    <div class="admin-page-header">
    </div>

    <!-- 추가/수정/삭제 버튼 -->
    <div class="admin-actions">
      <div class="admin-buttons">
        <Button variant="primary" on:click={openCreateModal}>회원 추가</Button>
        <Button variant="secondary" on:click={openEditModal} disabled={!selectedUser}>수정</Button>
        <Button variant="danger" on:click={openDeleteModal} disabled={!selectedUser || isAdminAccount(selectedUser)}>삭제</Button>
        <Button variant="info" on:click={restoreUser} disabled={!selectedUser}>복구</Button>
        <Button 
          variant={selectedUser && selectedUser.is_excluded ? "success" : "warning"} 
          on:click={toggleUserExcludeStatus} 
          disabled={!selectedUser}
        >
          {selectedUser && selectedUser.is_excluded ? '제외 해제' : '제외 설정'}
        </Button>
        <Button variant="primary" on:click={downloadExcel}>엑셀 다운로드</Button>
      </div>
      <div class="admin-search-container">
        <div class="deleted-users-toggle">
          <label class="toggle-label">
            <input
              type="checkbox"
              bind:checked={showDeletedUsers}
              on:change={fetchUsers}
            />
            삭제된 사용자 보기
          </label>
        </div>
        <input
          class="admin-search-input"
          type="text"
          placeholder="전체 검색..."
          bind:value={quickFilterText}
        />
      </div>
    </div>

    <!-- 일괄 처리 버튼 -->
    {#if selectedUsers.length > 0}
      <div class="bulk-actions">
        <div class="bulk-info">
          <span class="selected-count">{selectedUsers.length}개 선택됨</span>
        </div>
        <div class="bulk-buttons">
          <Button variant="danger" on:click={openBulkDeleteModal} disabled={hasAdminInSelection(selectedUsers)}>일괄 삭제</Button>
          <Button variant="warning" on:click={() => bulkUpdateExcludeStatus(true)}>일괄 제외</Button>
          <Button variant="success" on:click={() => bulkUpdateExcludeStatus(false)}>일괄 제외 해제</Button>
          <Button variant="secondary" on:click={() => bulkUpdateBlacklistStatus(true)}>일괄 블랙</Button>
          <Button variant="info" on:click={() => bulkUpdateBlacklistStatus(false)}>일괄 블랙 해제</Button>
        </div>
      </div>
    {/if}

    {#if isLoading}
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">데이터를 불러오는 중...</p>
      </div>
    {:else if loadError}
      <div class="error-container">
        <div class="error-icon">!</div>
        <h3 class="error-text">데이터를 불러오지 못했습니다</h3>
        <p>{loadError}</p>
        <button class="retry-btn" on:click={fetchUsers}>다시 시도</button>
      </div>
    {:else}
      <div class="admin-table-wrapper">
        <div class="ag-theme-alpine custom-grid" class:mobile-grid={isMobile} style="width: 100%; height: {isMobile ? 'auto' : '600px'};">
          <AgGridSvelte
            {rowData}
            {columnDefs}
            {gridOptions}
            pagination={true}
            paginationPageSize={paginationPageSize}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onGridReady={onGridReady}
            onSelectionChanged={onSelectionChanged}
            onCellClicked={onCellClicked}
          />
        </div>
      </div>
    {/if}
  </div>
</main>

<!-- 회원 추가 모달 -->
<FormModal
  bind:isOpen={isCreateModalOpen}
  title="회원 추가"
  submitText="추가"
  onSubmit={createUser}
  onCancel={() => (isCreateModalOpen = false)}
>
  <div class="form-group">
    <label for="create-email">이메일</label>
    <input id="create-email" type="email" bind:value={createForm.email} required />
  </div>
  <div class="form-group">
    <label for="create-password">비밀번호</label>
    <input id="create-password" type="password" bind:value={createForm.password} required />
  </div>
  <div class="form-group">
    <label for="create-name">이름</label>
    <input id="create-name" type="text" bind:value={createForm.name} />
  </div>
  <div class="form-group">
    <label for="create-phone">전화</label>
    <input id="create-phone" type="text" bind:value={createForm.phone} />
  </div>
  <div class="form-group">
    <label for="create-birth">생년월일</label>
    <input id="create-birth" type="date" bind:value={createForm.birthDate} />
  </div>
  <div class="form-group">
    <fieldset class="gender-fieldset">
      <legend class="form-label">성별</legend>
      <div>
        <label class="radio-container" style="margin-right:15px;">
          <input type="radio" name="createGender" value="M" bind:group={createForm.gender} id="admin-create-gender-m" />
          <span class="radio-custom"></span> 남
        </label>
        <label class="radio-container">
          <input type="radio" name="createGender" value="F" bind:group={createForm.gender} id="admin-create-gender-f" />
          <span class="radio-custom"></span> 여
        </label>
      </div>
    </fieldset>
  </div>
  <div class="form-group">
    <label for="create-member-type">회원유형</label>
    <select id="create-member-type" bind:value={createForm.member_type}>
      <option value="individual">일반</option>
      <option value="company">기업</option>
    </select>
  </div>
  <div class="form-group">
    <label for="create-is-blacklist">블랙</label>
    <input id="create-is-blacklist" type="checkbox" bind:checked={createForm.is_blacklist} />
  </div>
  <div class="form-group">
    <label for="create-blacklist-reason">비고</label>
    <textarea id="create-blacklist-reason" rows="2" bind:value={createForm.blacklist_reason}></textarea>
  </div>
  <div class="form-group">
    <label for="create-available-from">투입가능일자</label>
    <input id="create-available-from" type="date" bind:value={createForm.available_from} />
  </div>
</FormModal>

<!-- 회원 수정 모달 -->
<FormModal
  bind:isOpen={isEditModalOpen}
  title="회원 수정"
  submitText="저장"
  onSubmit={updateUser}
  onCancel={() => (isEditModalOpen = false)}
>
  <div class="form-group">
    <label for="edit-available-from">투입가능일자</label>
    <input id="edit-available-from" type="date" bind:value={editForm.available_from} />
  </div>
  <div class="form-group">
    <label for="edit-id">ID</label>
    <input id="edit-id" type="text" value={editForm.id} disabled />
  </div>
  <div class="form-group">
    <label for="edit-email">이메일</label>
    <input id="edit-email" type="email" value={editForm.email} disabled />
  </div>
  <div class="form-group">
    <label for="edit-name">이름</label>
    <input id="edit-name" type="text" bind:value={editForm.name} />
  </div>
  <div class="form-group">
    <label for="edit-phone">전화</label>
    <input id="edit-phone" type="text" bind:value={editForm.phone} />
  </div>
  <div class="form-group">
    <label for="edit-birth">생년월일</label>
    <input id="edit-birth" type="date" bind:value={editForm.birthDate} />
  </div>
  <div class="form-group">
    <fieldset class="gender-fieldset">
      <legend class="form-label">성별</legend>
      <div>
        <label class="radio-container" style="margin-right:15px;">
          <input type="radio" name="editGender" value="M" bind:group={editForm.gender} id="admin-edit-gender-m" />
          <span class="radio-custom"></span> 남
        </label>
        <label class="radio-container">
          <input type="radio" name="editGender" value="F" bind:group={editForm.gender} id="admin-edit-gender-f" />
          <span class="radio-custom"></span> 여
        </label>
      </div>
    </fieldset>
  </div>
  <div class="form-group">
    <label for="edit-member-type">회원유형</label>
    <select id="edit-member-type" bind:value={editForm.member_type}>
      <option value="individual">일반</option>
      <option value="company">기업</option>
      <option value="admin">관리자</option>
    </select>
  </div>
  <div class="form-group">
    <label for="edit-is-blacklist">블랙</label>
    <input id="edit-is-blacklist" type="checkbox" bind:checked={editForm.is_blacklist} />
  </div>
  <div class="form-group">
    <label for="edit-blacklist-reason">사유</label>
    <textarea id="edit-blacklist-reason" rows="3" bind:value={editForm.blacklist_reason}></textarea>
  </div>
</FormModal>

<!-- 회원 삭제 확인 모달 -->
<ConfirmModal
  bind:isOpen={isDeleteModalOpen}
  title="회원 삭제"
  message="정말로 이 회원을 삭제하시겠습니까? 삭제된 사용자 보기 체크 하면 복구 가능합니다."
  confirmText="삭제"
  cancelText="취소"
  danger={true}
  onConfirm={deleteUser}
  onCancel={() => (isDeleteModalOpen = false)}
/>

<!-- 일괄 삭제 확인 모달 -->
<ConfirmModal
  bind:isOpen={isBulkDeleteModalOpen}
  title="일괄 삭제"
  message="선택된 회원들을 모두 삭제하시겠습니까? 삭제 후 되돌릴 수 없습니다."
  confirmText="삭제"
  cancelText="취소"
  danger={true}
  onConfirm={bulkDeleteUsers}
  onCancel={() => (isBulkDeleteModalOpen = false)}
/>


<!-- 첨부 문서 모달 -->
{#if isProfileFilesModalOpen}
  <div class="modal-overlay" on:click={closeProfileFilesModal} on:keydown={(e) => e.key === 'Escape' && closeProfileFilesModal()}>
    <div class="modal-container profile-files-modal" on:click|stopPropagation on:keydown={(e) => e.key === 'Escape' && closeProfileFilesModal()}>
      <div class="modal-header">
        <h3>첨부 문서 {#if fileModalUser}- {fileModalUser.name || fileModalUser.email}{/if}</h3>
        <button class="close-modal-btn" on:click={closeProfileFilesModal}>✕</button>
      </div>
      <div class="modal-content">
        <!-- 기존 파일 목록 -->
        {#if isLoadingFiles}
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <div class="loading-text">파일을 불러오는 중...</div>
          </div>
        {:else if fileLoadError}
          <div class="error-container">
            <div class="error-icon">!</div>
            <div class="error-text">{fileLoadError}</div>
          </div>
        {:else if selectedUserFiles.length === 0}
          <div class="empty-state">
            <div class="empty-icon">📄</div>
            <p>업로드된 첨부 문서가 없습니다.</p>
          </div>
        {:else}
          <div class="files-list">
            <div class="files-header">
              <h4>업로드된 파일 ({selectedUserFiles.length}개)</h4>
            </div>
            <div class="files-grid">
              {#each selectedUserFiles as file (file.id)}
                <div class="file-item">
                  <div class="file-icon">
                    {#if file.mime_type?.includes('pdf')}
                      📄
                    {:else if file.mime_type?.includes('image')}
                      🖼️
                    {:else if file.mime_type?.includes('word') || file.mime_type?.includes('document')}
                      📝
                    {:else if file.mime_type?.includes('excel') || file.mime_type?.includes('sheet')}
                      📊
                    {:else}
                      📎
                    {/if}
                  </div>
                  <div class="file-info">
                    <div class="file-name" title={file.original_name}>{file.original_name}</div>
                    <div class="file-details">
                      <span class="file-size">
                        {#if file.file_size}
                          {(file.file_size / 1024 / 1024).toFixed(2)} MB
                        {:else}
                          크기 정보 없음
                        {/if}
                      </span>
                      <span class="file-type">{file.file_type || '일반'}</span>
                      <span class="file-date">
                        {#if file.created_at}
                          {new Date(file.created_at).toLocaleDateString('ko-KR')}
                        {:else}
                          날짜 정보 없음
                        {/if}
                      </span>
                    </div>
                    {#if file.description}
                      <div class="file-description">{file.description}</div>
                    {/if}
                  </div>
                  <div class="file-actions">
                    <button class="download-btn" on:click={() => downloadFile(file, fileModalUser)} title="파일 다운로드">⬇️ 다운로드</button>
                    <button class="delete-file-btn" on:click={() => deleteFileForUser(file)} title="파일 삭제">🗑️ 삭제</button>
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}

        <!-- 파일 업로드 영역 숨김 처리 (테스트 기능)
        <hr class="section-divider" />

        <div class="upload-section">
          <button
            type="button"
            class="upload-section-toggle"
            on:click={() => isUploadSectionOpen = !isUploadSectionOpen}
          >
            <span class="toggle-icon">{isUploadSectionOpen ? '▼' : '▶'}</span>
            <span> 파일 업로드(테스트)</span>
          </button>

          {#if isUploadSectionOpen}
            <div class="upload-section-content">
              <div
                class="file-drop-zone"
                class:drag-over={dragOver}
                on:dragover={handleUploadDragOver}
                on:dragleave={handleUploadDragLeave}
                on:drop={handleUploadDrop}
              >
                <input
                  bind:this={uploadFileInput}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.hwp,.hwpx,.txt,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
                  on:change={handleUploadFileSelect}
                  class="file-input-hidden"
                />
                <div class="drop-zone-content">
                  <div class="upload-icon">📁</div>
                  <p class="drop-text">
                    파일을 여기로 드래그하거나
                    <button type="button" class="browse-button" on:click={() => uploadFileInput.click()}>파일 선택</button>
                  </p>
                  <p class="file-info-text">최대 5개 파일, 파일당 최대 10MB</p>
                </div>
              </div>

              {#if uploadErrors.length > 0}
                <div class="upload-errors">
                  {#each uploadErrors as error}
                    <p class="upload-error-message">❌ {error}</p>
                  {/each}
                </div>
              {/if}

              {#if selectedUploadFiles.length > 0}
                <div class="selected-upload-files">
                  <h5>선택된 파일:</h5>
                  {#each selectedUploadFiles as file, index}
                    <div class="selected-file-item">
                      <span class="selected-file-icon">{getUploadFileIcon(file.name)}</span>
                      <div class="selected-file-details">
                        <span class="selected-file-name">{file.name}</span>
                        <span class="selected-file-size">{formatUploadFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        class="remove-file-button"
                        on:click={() => removeUploadFile(index)}
                        disabled={isUploading}
                      >✕</button>
                    </div>
                  {/each}
                  <div class="upload-actions">
                    <button
                      type="button"
                      class="upload-button"
                      disabled={isUploading}
                      on:click={uploadFilesForUser}
                    >
                      {#if isUploading}
                        <span class="upload-spinner"></span>
                        업로드 중...
                      {:else}
                        📤 파일 업로드
                      {/if}
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
        -->
      </div>
      <div class="modal-footer">
        <button class="modal-btn cancel" on:click={closeProfileFilesModal}>닫기</button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* 모바일 메뉴 */
  .mobile-menu {
    display: none;
    position: fixed;
    top: 0;
    right: -300px;
    width: 300px;
    height: 100vh;
    background-color: white;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease-in-out;
    z-index: 1001;
    padding: 20px;
  }

  .mobile-menu.open {
    right: 0;
  }

  /* 모바일에서만 메뉴 표시 */
  @media (max-width: 768px) {
    .mobile-menu {
      display: block;
    }
  }

  /* 모바일 메뉴 오버레이 */
  .mobile-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: none;
  }

  @media (max-width: 768px) {
    .mobile-menu-overlay {
      display: block;
    }
  }

  .close-btn {
    background: none;
    border: none;
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 1.5rem;
    cursor: pointer;
  }

  .mobile-menu ul {
    list-style: none;
    margin-top: 60px;
  }

  .mobile-menu li {
    margin-bottom: 20px;
  }

  .mobile-menu a {
    color: #333;
    text-decoration: none;
    font-size: 1.2rem;
    font-weight: 500;
  }

  .user-info {
    font-weight: bold;
    color: #3273dc;
    padding: 0 0 10px 0;
    border-bottom: 1px solid #eee;
    margin-bottom: 15px;
  }

  /* 관리자 페이지 스타일 */
  .admin-content {
    padding-top: 20px;
  }

  .admin-page-header {
    margin-bottom: var(--spacing-md);
  }

  /* 액션 버튼 영역 */
  .admin-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);
    gap: var(--spacing-md);
  }

  .admin-buttons {
    display: flex;
    gap: 0.5rem;
  }

  .admin-search-container {
    flex: 0 0 300px;
  }

  .admin-search-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    font-size: 0.9rem;
  }

  /* 테이블 래퍼 */
  .admin-table-wrapper {
    background: white;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    overflow: hidden;
  }

  /* 로딩 및 에러 상태 */
  .loading-container,
  .error-container {
    text-align: center;
    padding: var(--spacing-xl) 0;
  }

  .error-container {
    color: var(--danger-color);
  }

  .retry-btn {
    margin-top: var(--spacing-md);
    padding: 0.5rem 1rem;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  .retry-btn:hover {
    background: var(--primary-hover);
  }

  /* 반응형 스타일 */
  @media (max-width: 768px) {
    .admin-actions {
      flex-direction: column;
      align-items: stretch;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    .admin-buttons {
      order: 2;
      justify-content: center;
    }

    .admin-search-container {
      order: 1;
      flex: none;
      max-width: none;
    }

    .admin-search-input {
      font-size: 16px; /* iOS 줌 방지 */
    }
  }

  /* 첨부 문서 버튼 */
  :global(.profile-docs-btn) {
    border: none;
    padding: 15px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    transition: all 0.2s ease;
    font-weight: 600;
    min-width: 120px;
    height: 50px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  :global(.profile-docs-btn .doc-icon) {
    font-size: 18px;
    display: inline-block;
  }
  
  /* AG Grid 내부 버튼을 셀에 가득 차게 */
  :global(.ag-cell .profile-docs-btn) {
    width: 100% !important;
    height: 100% !important;
    min-width: unset !important;
    min-height: 35px !important;
    margin: 2px !important;
    box-sizing: border-box !important;
  }
  
  /* 파일이 있는 경우 (파란색) */
  :global(.profile-docs-btn[style*="#4A90E2"]) {
    background: #4A90E2 !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#4A90E2"]:hover) {
    background: #357ABD !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(74, 144, 226, 0.3);
  }
  
  /* 파일이 없는 경우 (회색) */
  :global(.profile-docs-btn[style*="#6c757d"]) {
    background: #6c757d !important;
    color: white !important;
  }
  :global(.profile-docs-btn[style*="#6c757d"]:hover) {
    background: #5a6268 !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(108, 117, 125, 0.3);
  }
  /* 첨부 문서 모달 */
  .profile-files-modal {
    max-width: 800px;
    max-height: 80vh;
  }
  .files-list {
    max-height: 400px;
    overflow-y: auto;
  }
  .files-header {
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;
  }
  .files-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .file-item {
    display: flex;
    align-items: center;
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    transition: background-color 0.2s;
  }
  .file-item:hover {
    background: #e9ecef;
  }
  .file-icon {
    font-size: 24px;
    margin-right: 12px;
    flex-shrink: 0;
  }
  .file-info {
    flex: 1;
    min-width: 0;
  }
  .file-name {
    font-weight: 600;
    color: #495057;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .file-details {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #6c757d;
    flex-wrap: wrap;
  }
  .file-details span {
    background: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  .file-description {
    margin-top: 6px;
    font-size: 14px;
    color: #6c757d;
    font-style: italic;
  }
  .file-actions {
    flex-shrink: 0;
    margin-left: 12px;
  }
  .download-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
  }
  .download-btn:hover {
    background: #218838;
  }

  .delete-file-btn {
    background: #dc3545;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
    margin-left: 6px;
  }

  .delete-file-btn:hover {
    background: #c82333;
  }

  /* ======= 모바일 카드 레이아웃 ======= */
  :global(.mobile-grid) {
    height: auto !important;
  }
  :global(.mobile-grid .ag-header) {
    display: none;
  }
  :global(.mobile-card) {
    padding: 12px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    margin: 3px 0;
  }
  :global(.mobile-card .card-header) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 8px;
  }
  :global(.mobile-card .name) {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 0;
  }
  :global(.mobile-card .card-body) {
    margin-top: 8px;
  }
  :global(.mobile-card .card-row) {
    padding: 1px 0;
    font-size: 13px;
    line-height: 1.4;
  }

  /* ===== 블랙리스트 태그 ===== */
  :global(.name-cell) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  :global(.member-blacklist-tag) {
    background-color: #dc3545;
    color: white;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
  }

  :global(.member-excluded-tag) {
    background-color: #fd7e14;
    color: white;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
    margin-left: 4px;
  }

  :global(.member-deleted-tag) {
    background-color: #6c757d;
    color: white;
    padding: 1px 6px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    flex-shrink: 0;
    margin-left: 4px;
  }

  :global(.badges) {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-left: auto;
  }

  :global(.attachment-row) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid #e9ecef;
  }

  /* 메인 컨테이너 스타일 추가 */
  .main-container {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0;
  }

  /* 체크박스 셀 스타일 */
  :global(.checkbox-cell) {
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
  }

  :global(.ag-selection-checkbox) {
    margin: 0 !important;
  }

  :global(.ag-header-select-all) {
    margin: 0 !important;
  }

  /* 일괄 처리 버튼 스타일 */
  .bulk-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: var(--spacing-lg);
    gap: var(--spacing-md);
  }

  .bulk-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .selected-count {
    font-weight: 600;
    color: var(--primary-color);
    background: white;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid var(--primary-color);
    font-size: 0.9rem;
  }

  .bulk-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  /* 모바일 체크박스 스타일 */
  :global(.checkbox-container) {
    display: flex;
    align-items: center;
    margin-right: 8px;
  }

  :global(.mobile-checkbox) {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--primary-color);
  }

  :global(.mobile-card .card-header .name) {
    flex: 1;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 0;
  }

  /* 삭제된 사용자 토글 */
  .deleted-users-toggle {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    color: var(--text-color);
    cursor: pointer;
  }

  .toggle-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
  }

  /* 반응형 일괄 처리 버튼 */
  @media (max-width: 768px) {
    .bulk-actions {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
    }

    .bulk-info {
      justify-content: center;
    }

    .bulk-buttons {
      justify-content: center;
      flex-wrap: wrap;
    }

    .bulk-buttons :global(.btn) {
      font-size: 0.8rem;
      padding: 6px 12px;
    }
  }

  /* 라디오 버튼 스타일 - 원 가운데 스타일 */
  :global(.radio-container) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    position: relative;
  }

  :global(.radio-container input[type="radio"]) {
    display: none;
  }

  :global(.radio-custom) {
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-radius: 50%;
    position: relative;
    display: inline-block;
    background-color: white;
    transition: all 0.3s ease;
  }

  :global(.radio-container input[type="radio"]:checked + .radio-custom) {
    border-color: var(--primary-color);
    background-color: var(--primary-color);
  }

  :global(.radio-custom::after) {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background-color: white;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  :global(.radio-container input[type="radio"]:checked + .radio-custom::after) {
    opacity: 1;
  }

  :global(.radio-container:hover .radio-custom) {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(50, 115, 220, 0.2);
  }

  /* ===== 파일 업로드 영역 스타일 ===== */
  .upload-section {
    margin-bottom: 16px;
  }

  .upload-section-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 16px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    transition: background-color 0.2s;
  }

  .upload-section-toggle:hover {
    background: #e5e7eb;
  }

  .toggle-icon {
    font-size: 0.8rem;
    color: #6b7280;
  }

  .upload-section-content {
    margin-top: 12px;
    padding: 16px;
    background: #fafafa;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
  }

  .upload-section h4 {
    margin: 0 0 12px 0;
    color: #374151;
    font-size: 1rem;
    font-weight: 600;
  }

  .file-drop-zone {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    transition: all 0.3s ease;
    background: #f9fafb;
    cursor: pointer;
    position: relative;
  }

  .file-drop-zone:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  .file-drop-zone.drag-over {
    border-color: #3b82f6;
    background: #dbeafe;
    transform: scale(1.01);
  }

  .file-input-hidden {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .drop-zone-content {
    pointer-events: none;
  }

  .upload-icon {
    font-size: 2rem;
    margin-bottom: 8px;
  }

  .drop-text {
    margin: 0 0 4px 0;
    color: #374151;
    font-size: 0.95rem;
  }

  .browse-button {
    color: #3b82f6;
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
    pointer-events: all;
    font-size: inherit;
    padding: 0;
  }

  .browse-button:hover {
    color: #2563eb;
  }

  .file-info-text {
    margin: 0;
    color: #6b7280;
    font-size: 0.8rem;
  }

  .upload-errors {
    margin: 12px 0;
  }

  .upload-error-message {
    color: #dc2626;
    font-size: 0.85rem;
    margin: 4px 0;
  }

  .selected-upload-files {
    margin: 12px 0;
    padding: 12px;
    background: #f3f4f6;
    border-radius: 6px;
  }

  .selected-upload-files h5 {
    margin: 0 0 8px 0;
    color: #374151;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .selected-file-item {
    display: flex;
    align-items: center;
    padding: 8px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    margin: 6px 0;
  }

  .selected-file-icon {
    font-size: 1.25rem;
    margin-right: 8px;
  }

  .selected-file-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .selected-file-name {
    font-weight: 500;
    color: #374151;
    font-size: 0.85rem;
  }

  .selected-file-size {
    color: #6b7280;
    font-size: 0.75rem;
  }

  .remove-file-button {
    background: none;
    border: none;
    font-size: 1rem;
    cursor: pointer;
    color: #9ca3af;
    padding: 4px 8px;
  }

  .remove-file-button:hover:not(:disabled) {
    color: #dc2626;
  }

  .remove-file-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .upload-actions {
    margin-top: 12px;
    text-align: center;
  }

  .upload-button {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 0.9rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background-color 0.2s;
  }

  .upload-button:hover:not(:disabled) {
    background: #2563eb;
  }

  .upload-button:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .upload-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #ffffff;
    border-radius: 50%;
    border-top-color: transparent;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .section-divider {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 20px 0;
  }

  /* 모바일 대응 */
  @media (max-width: 768px) {
    .file-drop-zone {
      padding: 16px;
    }

    .upload-icon {
      font-size: 1.5rem;
    }

    .selected-file-item {
      flex-direction: row;
    }
  }
</style> 